<script setup>
/**
 * YouAndThem: one "You & <name>" for the gift room (step 6) and the
 * Community (a seeker's Compatibility). Recipient polish 2026-11-03,
 * DESIGN.md §6, §19, §20.2 and the round 5 block; prototype.html
 * `compatHTML` and `skyArcs`.
 *
 * One scroll, nothing to tap to understand it:
 *   the pair line and "You & <name>", the how-to line, the three threads
 *   (names head the two columns; ONE plain sentence above each thread, after
 *   its numbered dot; thicker = stronger), the lead-in (the gift's day), the
 *   summary, three numbered cards with "Try this", then Go deeper (lighter,
 *   collapsed). Go deeper opens only when both people have a birth time and
 *   place; otherwise one locked line, who has what, and the way to fix it:
 *   "Add my birth time and place" (the built closer look, right here) or
 *   "Ask <name> for their birth time" (once per pair).
 *
 * `blank` draws the picture's empty threads (the Community before it is seen).
 * The words come from utils/you-and-them (the adapter over both payloads).
 */
import { computed, ref } from 'vue'
import BirthMoment from '@/components/widgets/BirthMoment.vue'
import { reducedMotionNow } from '@/composables/useReducedMotion'
import { HOWTO, DEEP_CLOSED, DEEP_LOCKED, ASK_SENT, threadLayout } from '@/utils/you-and-them'
import '@/assets/fonts/letter/letter-fonts.css'

const props = defineProps({
  // The view model (utils/you-and-them `youAndThem`).
  view: { type: Object, required: true },
  // 'gift' or 'community'.
  context: { type: String, default: 'gift' },
  // The gift's lead-in, on its own day ("Chris Lee was thinking of you today…").
  lead: { type: String, default: '' },
  // The empty picture (before it is seen).
  blank: { type: Boolean, default: false },
  // "Kept in your Journal, under You & <name>." (§7)
  kept: { type: Boolean, default: false },
  mystic: { type: String, default: '' },
  // The other person's time ask was sent (here or before).
  asked: { type: Boolean, default: false },
  savingTime: { type: Boolean, default: false },
  timeErrors: { type: Object, default: null },
  narrow: { type: Boolean, default: null },
})
const emit = defineEmits(['save-time', 'ask-time'])

const reduced = reducedMotionNow()
const isNarrow = computed(() => (props.narrow !== null ? props.narrow
  : (typeof window !== 'undefined' && window.innerWidth < 700)))
const v = computed(() => props.view || {})
const them = computed(() => (v.value.them && v.value.them.name) || 'them')
const THEM = computed(() => them.value.toUpperCase())
const lay = computed(() => threadLayout(props.blank ? [] : (v.value.threads || []), { narrow: isNarrow.value }))
const pairLine = computed(() => {
  const me = v.value.me || {}
  const a = me.name ? `${me.name}${me.glyph ? ' ' + me.glyph : ''}` : ''
  const b = `${them.value}${v.value.them && v.value.them.glyph ? ' ' + v.value.them.glyph : ''}`
  return a ? `${a} · ${b}` : b
})
const deep = computed(() => v.value.deep || {})
const addingTime = ref(false)
const isAsked = computed(() => props.asked || !!deep.value.asked)
// A starfield that is the same every time (no flicker between draws).
const stars = computed(() => {
  let seed = 7
  const rnd = () => (seed = (seed * 9301 + 49297) % 233280) / 233280
  return Array.from({ length: 40 }, () => ({
    x: (rnd() * lay.value.W).toFixed(0), y: (rnd() * lay.value.H).toFixed(0),
    r: (rnd() * 0.9 + 0.3).toFixed(1), o: (rnd() * 0.35 + 0.08).toFixed(2),
  }))
})
// A sparkle at each end of a thread.
const sparkle = (x, y, r = 7) => `M${x} ${y - r} Q${x} ${y} ${x + r} ${y} Q${x} ${y} ${x} ${y + r} Q${x} ${y} ${x - r} ${y} Q${x} ${y} ${x} ${y - r}Z`
</script>

<template>
  <div class="yt" :class="['yt-' + context, { blank }]" data-testid="you-and-them">
    <div class="yt-pair" data-testid="yt-pair">{{ pairLine }}</div>
    <h3 class="yt-h">You &amp; {{ them }}</h3>
    <p class="yt-sub">Where your stars meet</p>
    <p v-if="!blank" class="yt-howto" data-testid="yt-howto">{{ v.howto || HOWTO }}</p>

    <svg class="yt-sky" :class="{ grow: !reduced && !blank }" :viewBox="`0 0 ${lay.W} ${lay.H}`" role="img"
         :aria-label="`Three threads join your sky to ${them}'s.`" data-testid="yt-picture">
      <circle v-for="(s, i) in stars" :key="'s' + i" :cx="s.x" :cy="s.y" :r="s.r" fill="#efe4ff" :opacity="s.o" />
      <text class="yt-who" :x="lay.L" y="20" text-anchor="start" data-testid="yt-col-me">YOU<template v-if="v.me && v.me.sun"> · <tspan
        class="sym">{{ v.me.glyph }}</tspan> {{ v.me.sun.toUpperCase() }}</template></text>
      <text class="yt-who" :x="lay.R" y="20" text-anchor="end" data-testid="yt-col-them">{{ THEM }}<template v-if="v.them && v.them.sun"> · <tspan
        class="sym">{{ v.them.glyph }}</tspan> {{ v.them.sun.toUpperCase() }}</template></text>
      <template v-for="(r, i) in lay.rows" :key="'t' + i">
        <template v-if="blank || !r.sentence">
          <path :d="r.d" fill="none" stroke="rgba(203,184,238,.45)" stroke-width="1.2" stroke-dasharray="2 5" />
          <path :d="sparkle(lay.L, r.y)" fill="#cbb8ee" opacity=".6" /><path :d="sparkle(lay.R, r.y)" fill="#cbb8ee" opacity=".6" />
        </template>
        <g v-else class="yt-thread" data-testid="yt-thread">
          <path class="thr" :d="r.d" :stroke="r.color" :stroke-width="r.width" pathLength="100"
                :style="{ filter: `drop-shadow(0 0 5px ${r.color}aa)`, animationDelay: (400 + i * 380) + 'ms' }" />
          <path :d="sparkle(lay.L, r.y)" fill="#fff4dc" /><path :d="sparkle(lay.R, r.y)" fill="#fff4dc" />
          <g class="ph" :style="{ animationDelay: (900 + i * 380) + 'ms' }">
            <circle :cx="r.dot.x" :cy="r.dot.y" r="9" :fill="r.color" />
            <text class="yt-n" :x="r.dot.x" :y="Number(r.dot.y) + 3.5" text-anchor="middle">{{ r.n }}</text>
            <text class="yt-line" :x="r.text.x" :y="r.text.y" text-anchor="middle" data-testid="yt-sentence">{{ r.sentence }}</text>
          </g>
        </g>
      </template>
    </svg>

    <template v-if="!blank">
      <p v-if="lead" class="yt-lead" data-testid="yt-lead">{{ lead }}</p>
      <p v-if="v.summary" class="yt-summary" data-testid="yt-summary">{{ v.summary }}</p>

      <div v-if="v.threads && v.threads.length" class="yt-cards">
        <div v-for="t in v.threads" :key="t.n" class="yt-card" data-testid="yt-card">
          <span class="n" :style="{ background: t.color }">{{ t.n }}</span>
          <span>
            <b>{{ t.heading }}</b>
            <span v-if="t.picture" class="pic">{{ t.picture }}</span>
            <span v-else class="pic">{{ t.sentence }}.</span>
            <span v-if="t.tryThis" class="try" data-testid="yt-try"><i>Try this:</i> {{ t.tryThis }}</span>
          </span>
        </div>
      </div>

      <!-- Go deeper, lighter (§20.2): open only with both times and places. -->
      <details v-if="deep.open" class="yt-deep" data-testid="yt-deep">
        <summary><span class="ey">Go deeper</span><span>{{ deep.teaser || DEEP_CLOSED }}</span></summary>
        <p v-for="(line, i) in deep.risings" :key="'r' + i" class="one">{{ line }}</p>
        <div class="themes">
          <details v-for="th in deep.themes" :key="th.title" class="theme" data-testid="yt-theme">
            <summary><b>{{ th.title }}</b><span>{{ th.count }}</span></summary>
            <ul>
              <li v-for="(l, k) in th.lines" :key="k" :class="l.nature">
                <span class="dbar"><i :style="{ width: Math.round(l.strength * 100) + '%' }"></i></span><span>{{ l.text }}</span>
              </li>
            </ul>
          </details>
          <details v-if="deep.report" class="theme" data-testid="yt-full">
            <summary><b>The full reading</b><span>a few paragraphs</span></summary>
            <p class="full">{{ deep.report }}</p>
          </details>
        </div>
      </details>
      <div v-else class="yt-deep locked" data-testid="yt-deep-locked">
        <div class="dh"><span class="ey">Go deeper</span><span class="lock" aria-hidden="true">🔒︎</span></div>
        <p data-testid="yt-locked-line">{{ deep.lockedLine || DEEP_LOCKED }}</p>
        <ul class="have">
          <li><b>You:</b> {{ deep.have && deep.have.me }}</li>
          <li><b>{{ them }}:</b> {{ deep.have && deep.have.them }}</li>
        </ul>
        <template v-if="deep.missing === 'mine' || deep.missing === 'both'">
          <BirthMoment v-if="addingTime" mode="plain" :fields="['time', 'place']" :explain="false" :closable="false"
                       :saving="savingTime" :server-errors="timeErrors" submit-label="✦ Show me the full picture"
                       lock="Private. Only your signs show." @save="(out) => emit('save-time', out)" />
          <button v-else type="button" class="yt-quiet" data-testid="yt-add-time" @click="addingTime = true">Add my birth time and place</button>
        </template>
        <template v-else-if="deep.missing === 'theirs' && (deep.canAsk || isAsked)">
          <p v-if="isAsked" class="asked" data-testid="yt-asked">{{ ASK_SENT }}</p>
          <template v-else>
            <button type="button" class="yt-quiet" data-testid="yt-ask-time" @click="emit('ask-time')">Ask {{ them }} for their birth time</button>
            <p class="fine">One message, sent once. Nothing else is asked.</p>
          </template>
        </template>
        <!-- Decision 10: the date-only contacts stay visible meanwhile. -->
        <div v-if="deep.themes && deep.themes.length" class="themes" data-testid="yt-date-themes">
          <h4>What your birthdays already show</h4>
          <details v-for="th in deep.themes" :key="th.title" class="theme" data-testid="yt-theme">
            <summary><b>{{ th.title }}</b><span>{{ th.count }}</span></summary>
            <ul>
              <li v-for="(l, k) in th.lines" :key="k" :class="l.nature">
                <span class="dbar"><i :style="{ width: Math.round(l.strength * 100) + '%' }"></i></span><span>{{ l.text }}</span>
              </li>
            </ul>
          </details>
        </div>
      </div>

      <p v-if="kept" class="yt-kept" data-testid="yt-kept">Kept in your Journal, under You &amp; {{ them }}.</p>
      <div v-if="mystic" class="yt-mys">{{ mystic }}</div>
    </template>
    <slot />
  </div>
</template>

<style scoped>
.yt { --display: 'Letter Display', 'Cinzel', Georgia, serif; --serif: 'Letter Serif', 'Cormorant Garamond', Georgia, serif;
  --ui: Inter, system-ui, -apple-system, 'Segoe UI', sans-serif;
  position: relative; border-radius: 22px; padding: 22px 22px 18px; overflow: hidden; color: #e9dcff; text-align: center;
  background: radial-gradient(110% 80% at 50% 40%, rgba(123,75,176,.42), rgba(20,7,31,.96) 70%); border: 1px solid rgba(240,217,163,.38);
  box-shadow: 0 24px 60px -26px rgba(240,217,163,.35); }
.yt-pair { font: 600 12px var(--display); letter-spacing: .22em; color: #e7c983; text-transform: uppercase; font-variant-emoji: text; }
.yt-h { margin: 4px 0 0; font: 600 30px var(--display); color: #fff1cf; }
.yt-sub { margin: 2px 0 6px; font: italic 500 16px var(--serif); color: #cbb8ee; }
.yt-howto { max-width: 520px; margin: 6px auto 10px; font: 500 14px/1.45 var(--ui); color: #d8c9f2; }
.yt-sky { display: block; width: 100%; max-width: 560px; margin: 4px auto 6px; overflow: visible; }
.yt-who { font: 600 11px var(--display); letter-spacing: .16em; fill: #e7c983; }
.yt-who .sym { font-family: 'Apple Symbols', 'Segoe UI Symbol', 'Noto Sans Symbols 2', serif; }
.yt-line { font: 600 16px var(--ui); fill: #fff4dc; paint-order: stroke; stroke: #1d0d33; stroke-width: 3px; stroke-linejoin: round; }
.yt-n { font: 700 10.5px var(--ui); fill: #1a0a2e; }
.thr { fill: none; stroke-linecap: round; }
.grow .thr { stroke-dasharray: 100; animation: yt-draw 1s cubic-bezier(.4,.1,.2,1) both; }
.grow .ph { animation: yt-fade .5s ease both; }
@keyframes yt-draw { from { stroke-dashoffset: 100; } to { stroke-dashoffset: 0; } }
@keyframes yt-fade { from { opacity: 0; } to { opacity: 1; } }
.yt-lead { margin: 6px auto 6px; max-width: 520px; font: 500 14.5px var(--ui); color: #d8c9f2; }
.yt-summary { margin: 4px auto 14px; max-width: 540px; font: italic 500 20px/1.45 var(--serif); color: #fff6e6; }
.yt-cards { display: grid; gap: 10px; max-width: 540px; margin: 0 auto; text-align: left; }
.yt-card { display: grid; grid-template-columns: 26px 1fr; gap: 12px; align-items: start; padding: 12px 14px; border-radius: 14px;
  border: 1px solid rgba(240,217,163,.16); background: rgba(10,4,24,.38); }
.yt-card .n { width: 26px; height: 26px; border-radius: 50%; display: grid; place-items: center; font: 700 12px var(--ui); color: #1a0a2e; }
.yt-card b { display: block; margin-bottom: 4px; font: 600 14px var(--ui); color: #f3e8ff; }
.yt-card .pic { display: block; font: italic 500 18px/1.38 var(--serif); color: #fff6e6; }
.yt-card .try { display: block; margin-top: 8px; font: 500 14px/1.45 var(--ui); color: #e9dcff; }
.yt-card .try i { font-style: normal; font-weight: 700; color: #f0d9a3; margin-right: 2px; }
.yt-deep { margin: 16px auto 4px; max-width: 540px; text-align: left; padding: 12px 14px; border-radius: 14px;
  border: 1px solid rgba(240,217,163,.3); background: rgba(10,4,24,.45); }
.yt-deep > summary { list-style: none; cursor: pointer; display: grid; gap: 2px; }
.yt-deep > summary::-webkit-details-marker { display: none; }
.yt-deep > summary span:last-child { font: 600 14px var(--ui); color: #f3e8ff; }
.yt-deep .ey { font: 600 10.5px var(--display); letter-spacing: .18em; color: #e7c983; text-transform: uppercase; }
.yt-deep p { margin: 0 0 6px; font: 400 14.5px/1.5 var(--ui); color: #e3d8f5; }
.yt-deep .one { margin: 8px 0 0; }
.yt-deep.locked .dh { display: flex; justify-content: space-between; }
.yt-deep .lock { opacity: .7; }
.yt-deep .have { margin: 6px 0 10px; padding-left: 18px; font: 400 14px/1.5 var(--ui); color: #d8c9f2; }
.yt-deep .asked { color: #ffe7b5; font-weight: 600; }
.yt-deep .fine { margin-top: 6px; font: 400 12.5px var(--ui); color: #9b86c9; }
.themes { display: grid; gap: 6px; margin-top: 12px; }
.themes h4 { margin: 4px 0 2px; font: 600 10.5px var(--display); letter-spacing: .18em; color: #e7c983; text-transform: uppercase; }
.theme { border: 1px solid rgba(155,134,201,.25); border-radius: 10px; padding: 8px 10px; }
.theme summary { list-style: none; cursor: pointer; display: flex; justify-content: space-between; gap: 10px; }
.theme summary::-webkit-details-marker { display: none; }
.theme summary b { font: 600 14px var(--ui); color: #fff; }
.theme summary span { font: italic 500 14.5px var(--serif); color: #cbb8ee; }
.theme ul { list-style: none; margin: 8px 0 0; padding: 0; display: grid; gap: 6px; }
.theme li { display: grid; grid-template-columns: 54px 1fr; gap: 10px; align-items: center; font: 400 14px var(--ui); color: #f3e8ff; }
.theme .full { margin-top: 8px; }
.dbar { height: 6px; border-radius: 3px; background: rgba(185,166,214,.18); overflow: hidden; }
.dbar i { display: block; height: 100%; }
.theme li.easy .dbar i { background: #f0d9a3; } .theme li.tense .dbar i { background: #c9a2f2; }
.yt-quiet { display: inline-flex; align-items: center; justify-content: center; min-height: 44px; padding: 9px 18px; border-radius: 999px;
  background: transparent; border: 1px solid rgba(233,220,255,.22); color: #d8c9f2; font: 500 14px var(--ui); cursor: pointer; }
.yt-quiet:focus-visible { outline: 2px solid #f0d9a3; outline-offset: 3px; }
.yt-kept { margin: 14px 0 0; font: 500 13px var(--ui); color: #9b86c9; }
.yt-mys { margin-top: 12px; font: 600 12px var(--ui); color: #9b86c9; }
@media (max-width: 699px) {
  .yt { padding: 18px 12px 16px; } .yt-h { font-size: 26px; } .yt-summary { font-size: 18.5px; }
  .yt-line { font-size: 15px; } .yt-who { font-size: 13px; } .yt-card .pic { font-size: 17px; }
}
@media (prefers-reduced-motion: reduce) { .grow .thr, .grow .ph { animation: none; } }
</style>
